/*
 * Created on Sat Apr 03 2021
 *
 * Copyright (c) 2021 Coding House LLC
 */
import { RoutineInflatedModel } from "../models/RoutineModel";
import {
    AccessModel,
    EntityAttachment,
    ContentReadResponse,
    ContentResponse,
    EntityReference,
    isContentReadResponse,
    TopicTagRef,
} from "../models/content";
import { getLocaleCode } from "../utils/Utilities";
import { StrongFoundationService } from "./StrongFoundationService";
import { BuilderEntitySearchResult } from "../screens/create/types";
import { DateTime } from "luxon";

export class RoutineService extends StrongFoundationService {
    public constructor() {
        super();
    }

    public fetchRoutineResponse = async (id: string): Promise<ContentResponse<RoutineInflatedModel> | undefined> => {
        try {
            return await this.get<ContentResponse<RoutineInflatedModel>>(`routine/${id}`, {
                language: getLocaleCode(),
            });
        } catch (ex) {
            return undefined;
        }
    };

    public fetchRoutine = async (id: string): Promise<RoutineInflatedModel | undefined> => {
        try {
            const response = await this.fetchRoutineResponse(id);
            if (!response) {
                return undefined;
            }

            if (isContentReadResponse<RoutineInflatedModel>(response)) {
                return response.data;
            }

            return response;
        } catch (ex) {
            return undefined;
        }
    };

    public fetchAuthorRoutines = async (
        authorId: string,
        isOwner: boolean,
        afterDate = DateTime.utc().toMillis(),
        skip = 0,
    ): Promise<RoutineInflatedModel[]> => {
        try {
            const response = await this.get<RoutineInflatedModel[]>(`routine/author/${authorId}`, {
                afterDate,
                skip,
                isPublished: !isOwner,
                language: getLocaleCode(),
            });
            return response ?? [];
        } catch {
            return [];
        }
    };

    public createRoutine = async (input: {
        _id?: string;
        title: string;
        headerImage: string;
        content: string;
        entries: Array<{
            label: string;
            content: Array<{ id: string; type: string }>;
        }>;
        reminder: number;
        reminderStartTime: string | null;
        authorId: string;
        attachments?: EntityAttachment[];
        topicTags: TopicTagRef[];
        entityRefs: EntityReference[];
        accessModel: AccessModel;
        dateCreated?: number;
    }): Promise<RoutineInflatedModel | undefined> => {
        const body = {
            ...input,
            isPublished: true,
            dateCreated: input.dateCreated ?? DateTime.utc().toMillis(),
        };
        return this.post<RoutineInflatedModel>("routine", {routine: body});
    };

    public uploadRoutineHeaderImage = async (routineId: string, file: File): Promise<RoutineInflatedModel | undefined> => {
        const formData = new FormData();
        formData.append("headerImage", file);
        return this.postFormData<RoutineInflatedModel>(`routine/image/${routineId}`, formData);
    };

    public searchRoutinesByName = async (query: string): Promise<BuilderEntitySearchResult[]> => {
        if (!query.trim()) {
            return [];
        }
        try {
            const response = await this.get<RoutineInflatedModel[]>("routine", {
                afterDate: DateTime.utc().toMillis(),
                skip: 0,
                isPublished: true,
                language: getLocaleCode(),
            });

            return (response ?? [])
                .filter((routine) => (routine.title ?? "").toLowerCase().includes(query.trim().toLowerCase()))
                .map((routine) => ({
                    entityId: String(routine._id ?? ""),
                    entityType: "routine",
                    displayName: routine.title || "Routine",
                    subtitle: "Routine",
                    imageUrl: routine.headerImage,
                }))
                .filter((item) => !!item.entityId);
        } catch {
            return [];
        }
    };
}

export const routineService = new RoutineService();
